import { CHANGELOG, type ChangeKind, GAME_VERSION } from '@shefira/shared';

import { language } from '../i18n/language.js';
import { isNativeApp } from '../platform/native.js';
import { discordLink } from './discord.js';
import { closeButton } from './hud.js';
import { element } from './kit.js';
import { uiDate, uiFormat, uiText } from './text.js';
import './changelog.css';

/**
 * The version line of the screens before the game: the beta badge sits on
 * the logo, this line under it names the version, opens the changelog and
 * leads to the community's Discord.
 */
export function versionLine({ discord = true }: { discord?: boolean } = {}): HTMLElement {
  const line = element('p', 'version-line');
  line.append(
    element(
      'span',
      'version-line__number',
      uiFormat('Version {version}', { version: GAME_VERSION }),
    ),
  );
  const open = element('button', 'version-line__open', 'Nouveautés');
  open.type = 'button';
  open.addEventListener('click', () => {
    showChangelog();
  });
  line.append(element('span', 'version-line__sep', '·'), open);
  // The welcome has its own, larger invitation under the form.
  if (discord) line.append(element('span', 'version-line__sep', '·'), discordLink());
  return line;
}

const KIND_LABELS: Record<ChangeKind, string> = {
  new: 'Nouveau',
  improved: 'Amélioré',
  fixed: 'Corrigé',
};

function formatDate(iso: string): string {
  // Read in UTC: the release day stays that day whatever the player's time zone.
  return uiDate(`${iso}T12:00:00Z`, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * The changelog is written in both languages (ADR 0069): the English is set
 * as it is, never looked up in the interface catalogue; a line without its
 * English falls back to the French.
 */
function inLanguage(french: string, english: string | undefined): string {
  return language() === 'en' && english !== undefined ? english : uiText(french);
}

function textElement(tag: 'p' | 'span', className: string, text: string): HTMLElement {
  const node = element(tag, className);
  node.textContent = text;
  return node;
}

/** Every version, newest first; its list scrolls, never the window. */
export function showChangelog(): void {
  const root = element('div', 'changelog');
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', uiText('Nouveautés'));

  const frame = element('section', 'changelog__window');
  const list = element('div', 'changelog__list');
  // The apps sell nothing (ADR 0075): what speaks of the shop stays on the web.
  const app = isNativeApp();
  for (const entry of CHANGELOG) {
    const shown = app ? entry.changes.filter((change) => change.webOnly !== true) : entry.changes;
    if (shown.length === 0) continue;
    const block = element('article', 'changelog__entry');
    const heading = element(
      'h3',
      'changelog__version',
      uiFormat('Version {version}', { version: entry.version }),
    );
    heading.append(element('span', 'changelog__date', formatDate(entry.date)));
    block.append(heading);
    if (entry.title !== undefined && !(app && entry.webOnlyTitle === true))
      block.append(textElement('p', 'changelog__title', inLanguage(entry.title, entry.titleEn)));
    const changes = element('ul', 'changelog__changes');
    for (const change of shown) {
      const item = element('li', 'changelog__change');
      item.append(
        element(
          'span',
          `changelog__kind changelog__kind--${change.kind}`,
          KIND_LABELS[change.kind],
        ),
        textElement('span', 'changelog__text', inLanguage(change.text, change.en)),
      );
      changes.append(item);
    }
    block.append(changes);
    list.append(block);
  }

  const close = (): void => {
    window.removeEventListener('keydown', onKeyDown, true);
    root.remove();
  };
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    close();
  };

  frame.append(
    element('h2', 'changelog__heading', 'Nouveautés'),
    list,
    closeButton('Fermer (Échap)', close),
  );
  root.append(frame);
  root.addEventListener('click', (event) => {
    if (event.target === root) close();
  });
  document.body.append(root);
  window.addEventListener('keydown', onKeyDown, true);
}
